import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { TransitionGroup } from 'react-transition-group';
import {
  AutomationResponse,
  AutomationResponseAutomationBlocker,
} from '@rossum/api-client/queues';
import { IconCustomMenuDown, IconCustomMenuUp } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  Chip,
  Collapse,
  GridLegacy,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { boldText, white } from '../../../../lib/formaterValues';
import { snakeToSpaces, upperFirst } from '../../../../lib/keyConvertor';
import { AutomationSchemaMap } from './buildAutomationSchemaMap';
import { roundToFixedPercents } from './utils';

const DISPLAYED_BLOCKERS_COUNT = 5;

const shouldDisplayShowMoreLessButton = (
  automationBlockers: AutomationResponse['automationBlockers']
) => automationBlockers.length > DISPLAYED_BLOCKERS_COUNT;

type Props = {
  data?: AutomationResponse;
  schemaMap: AutomationSchemaMap;
  dateRangeLabel: string;
};

const getTypeName = (type: AutomationResponseAutomationBlocker['type']) =>
  upperFirst(snakeToSpaces(type));

const blockedAutomationFraction = (data: AutomationResponse) =>
  1 - data.totals.automation;

const AutomationBlockers = ({ data, schemaMap, dateRangeLabel }: Props) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const blockers = useMemo(
    () =>
      R.pipe(
        data?.automationBlockers ?? [],
        R.sortBy(blocker => blocker.documentOccurrences),
        R.reverse()
      ),
    [data?.automationBlockers]
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <Typography variant="h6">
          {intl.formatMessage({
            id: 'containers.settings.automation.automationBlockers.title',
          })}
        </Typography>
        <Chip
          label={intl.formatMessage({ id: 'component.betaChip.label' })}
          color="warning"
        />
      </Stack>

      {data && !!data.automationBlockers.length ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" paragraph>
            <Tooltip
              placement="top"
              title={roundToFixedPercents(blockedAutomationFraction(data), 0)}
            >
              <Chip
                component="span"
                label={(
                  blockedAutomationFraction(data) * data.totals.documentCount
                ).toFixed(0)}
              />
            </Tooltip>
            {` `}
            {intl.formatMessage(
              {
                id: 'containers.settings.automation.automationBlockers.description',
              },
              {
                boldText,
                dateRange: dateRangeLabel.toLowerCase(),
                total: data.totals.documentCount,
              }
            )}
          </Typography>

          <TransitionGroup>
            {blockers
              .slice(0, expanded ? undefined : DISPLAYED_BLOCKERS_COUNT)
              .map(blocker => {
                return (
                  <Collapse
                    key={`${blocker.level}-${blocker.type}-${blocker.id}`}
                  >
                    <GridLegacy container spacing={3} sx={{ marginBottom: 2 }}>
                      <GridLegacy item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {`${roundToFixedPercents(
                            blocker.documentOccurrencesRel,
                            0
                          )} (${blocker.documentOccurrences})`}
                        </Typography>
                      </GridLegacy>
                      <GridLegacy item xs={8}>
                        <Typography variant="body2" component="span">
                          {getTypeName(blocker.type)}{' '}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                          >
                            {blocker.id
                              ? intl.formatMessage(
                                  {
                                    id: 'containers.settings.automation.automationBlockers.datapointLevel',
                                  },
                                  {
                                    datapointLabel:
                                      schemaMap.get(blocker.id)?.label ??
                                      blocker.id,
                                    white,
                                  }
                                )
                              : intl.formatMessage({
                                  id: 'containers.settings.automation.automationBlockers.annotationLevel',
                                })}
                          </Typography>
                        </Typography>
                      </GridLegacy>
                    </GridLegacy>
                  </Collapse>
                );
              })}
          </TransitionGroup>

          {shouldDisplayShowMoreLessButton(data.automationBlockers) && (
            <GridLegacy>
              <GridLegacy item xs={3} />
              <GridLegacy item xs={8}>
                <Button
                  color="secondary"
                  onClick={() => setExpanded(!expanded)}
                  startIcon={
                    <SvgIcon fontSize="large">
                      {expanded ? <IconCustomMenuUp /> : <IconCustomMenuDown />}
                    </SvgIcon>
                  }
                  size="small"
                  sx={{ left: -8 }}
                >
                  {intl.formatMessage({
                    id: !expanded
                      ? 'containers.settings.automation.automationBlockers.showAll'
                      : 'containers.settings.automation.automationBlockers.showLess',
                  })}
                </Button>
              </GridLegacy>
            </GridLegacy>
          )}
        </Stack>
      ) : (
        <Box textAlign="center" sx={{ paddingTop: 2 }}>
          <Typography variant="body2" paragraph>
            {intl.formatMessage({
              id: 'containers.settings.automation.automationData.table.noData',
            })}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default AutomationBlockers;
